'use client';

/**
 * CodeForm - 코드 등록 폼 컴포넌트
 *
 * 사용자가 팬카드 코드를 입력하거나 OCR(카메라)로 스캔하여 디지털 카드를 획득하는 폼
 *
 * ## 카드 획득 플로우
 * 1. 코드 입력 또는 OCR 스캔
 * 2. API 호출로 카드 민팅
 * 3. 카드 등급(Special/Basic)과 첫 획득 여부에 따라 다른 모달 표시
 *
 * ## 모달 분기 로직
 * | 등급    | 첫 카드 | 결과                               |
 * |---------|---------|-----------------------------------|
 * | Special | O       | 비디오 재생 → CardAcquisitionModal |
 * | Special | X       | CardAcquisitionModal               |
 * | Basic   | O       | CardAcquisitionModal               |
 * | Basic   | X       | CardUnlockSuccessModal             |
 */

import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { OnScanCancelProps, OnScanCompleteProps } from 'src/app/_components/Modal/OCRModal/types';
import { useAppDispatch } from 'src/store/client/config';
import { modalActions } from 'src/store/client/slices/modalSlice';
import { checkLogin } from 'src/utils/auth';
import { getAxiosErrorStatus } from 'src/utils/client';
import { momentTimezone } from 'src/utils/time';

import Button from '@/app/(football)/[team]/_components/common/Button';
import CardAcquisitionModal from '@/app/(football)/[team]/_components/common/CardAcquisitionModal';
import FullScreenVideo from '@/app/(football)/[team]/_components/common/FullScreenVideo';
import Input from '@/app/(football)/[team]/_components/common/Input';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import CardUnlockSuccessModal from '@/app/(football)/[team]/main/_components/Modal/CardUnlockSuccessModal';
import type { VerifyCodeErrorStatusCode } from '@/app/(football)/[team]/main/_components/Modal/CodeVerificationFailModal';
import CodeVerificationFailModal from '@/app/(football)/[team]/main/_components/Modal/CodeVerificationFailModal';
import { trackEvent } from '@/libs/analytics/events';
import type { DigitalCardGrade } from '@/store/server/common/digital-card/digital-card.dto';
import { USE_DIGITAL_CARD_QUERY_KEY, useMintWithCodeMutation } from '@/store/server/common/digital-card/digital-card.query';
import { digitalCardService } from '@/store/server/common/digital-card/digital-card.service';
import { USE_FOOTBALL_QUERY_KEY } from '@/store/server/common/football/football.query';
import { useGetOriginalsInitQuery } from '@/store/server/common/originals/originals.query';

/**
 * 카드 획득 처리에 필요한 최소 데이터
 * - CodeMintingResponse의 모든 필드가 아닌 실제 사용하는 필드만 정의
 * - OCR과 코드 입력 모두 동일한 인터페이스 사용
 */
interface CardAcquisitionData {
  card_name: string;
  image: string;
  metadata_idx: number;
  pack_idx: number;
  grade: DigitalCardGrade;
  design_idx: number | null;
  point: number;
  total_point: number;
}

const CODE_MIN_LENGTH = 8;
const CODE_MAX_LENGTH = 10;

export default function CodeForm() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { originalsName, teamSlug } = useTeamConfig();
  // ============================================================================
  // State
  // ============================================================================

  /** 입력된 코드 (대문자 영숫자) */
  const [code, setCode] = useState('');

  /** Special + 첫 카드 시 비디오 재생 상태 */
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  /** 비디오 재생 후 모달에 표시할 카드 데이터 */
  const pendingCardDataRef = useRef<CardAcquisitionData | null>(null);

  // ============================================================================
  // 오리지널스 초기 데이터 조회
  // ============================================================================

  const { data: initData, isError: isInitError } = useGetOriginalsInitQuery({
    originalsNameList: [originalsName],
  });

  /** 코드 등록 API에 전달할 오리지널스 식별자 */
  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  /** 이벤트 시작/종료 시간 (Unix timestamp) */
  const startDateTime = initData?.[originalsName]?.startDateTime ?? 0;
  const endDateTime = initData?.[originalsName]?.endDateTime ?? 0;

  // ============================================================================
  // 이벤트 상태 판단
  // ============================================================================

  /** 이벤트 진행 상태: 'before' | 'available' | 'after' */
  const eventStatus = useMemo<'before' | 'after' | 'available'>(() => {
    if (!startDateTime || !endDateTime) return 'before';

    const now = momentTimezone();
    const isBeforeStart = momentTimezone(startDateTime).isAfter(now);
    const isEnded = momentTimezone(endDateTime).isBefore(now);

    return isBeforeStart ? 'before' : !isEnded ? 'available' : 'after';
  }, [startDateTime, endDateTime]);

  /** 버튼 비활성화 여부 (이벤트 진행 중이 아니거나 초기 데이터 에러) */
  const isButtonDisabled = eventStatus !== 'available' || isInitError;

  // ============================================================================
  // 공통 핸들러
  // ============================================================================

  /**
   * 로그인 체크 후 콜백 실행
   * - 로그인 상태면 콜백 즉시 실행
   * - 비로그인이면 로그인 유도 모달 표시
   */
  const requireLogin = (callback: () => void) => {
    if (checkLogin()) {
      callback();
      return;
    }

    dispatch(
      modalActions.openConfirmModal({
        icon: 'danger',
        title: ['먼저 로그인 해주세요.'],
        description: ['로그인 후 코드 등록을 진행해주세요.'],
        confirmText: ['로그인하기'],
        closeText: ['확인'],
        onConfirm: () => router.push(`/account/login?retUrl=${pathname}`),
      }),
    );
  };

  /**
   * 코드 등록 에러 처리
   * @param statusCode - API 에러 코드
   * @param scannedCode - OCR 스캔 시 인식된 코드 (입력창에 채워줌)
   */
  const handleCodeError = (statusCode: string, scannedCode?: string) => {
    // 3002: 코드 등록 기간 아님
    if (statusCode === '3002') {
      dispatch(
        modalActions.openAlertModal({
          icon: 'danger',
          title: ['코드 등록기간이 아닙니다.'],
          onClose: () => setCode(''),
        }),
      );
      return;
    }

    // 그 외 에러: 코드 검증 실패 모달
    dispatch(
      modalActions.openComponentModal({
        component: <CodeVerificationFailModal statusCode={statusCode as VerifyCodeErrorStatusCode} />,
      }),
    );

    // OCR 스캔 실패 시 인식된 코드를 입력창에 채워줌 (사용자가 수정 가능)
    if (scannedCode) setCode(scannedCode);
  };

  // ============================================================================
  // 카드 획득 성공 처리
  // ============================================================================

  /** 민팅 성공 후 공통 처리: 코드 초기화 및 관련 쿼리 갱신 */
  const handleSuccessMint = () => {
    setCode('');
    queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getTopCollector] });
    queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getRealTimeCard] });
    queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getRealTimeEdition] });
    queryClient.invalidateQueries({ queryKey: [USE_DIGITAL_CARD_QUERY_KEY.getMyCardCollection] });
  };

  /**
   * 카드 획득 성공 시 분기 처리
   * 1. 카드 상세 API로 첫 획득 여부 확인 (amount === 1)
   * 2. 등급과 첫 획득 여부에 따라 비디오 또는 모달 표시
   */
  const handleCardAcquisition = async (data: CardAcquisitionData) => {
    // design_idx 없음 → 2025 팩 (등급 없음) → 이미지 기반 모달
    if (data.design_idx == null) {
      dispatch(
        modalActions.openComponentModal({
          component: (
            <CardUnlockSuccessModal
              cardName={data.card_name}
              image={data.image}
              metadataIdx={data.metadata_idx}
              packIdx={data.pack_idx}
              point={data.point}
              totalPoint={data.total_point}
              teamSlug={teamSlug}
            />
          ),
        }),
      );
      handleSuccessMint();
      return;
    }

    // design_idx 있음 → 2026 팩 (등급 분기)
    const detailRes = await digitalCardService.getMyCardDetail({ metadata_idx: data.metadata_idx });
    const isFirstCard = detailRes.data.data.amount === 1;
    const isSpecial = data.grade === 'Special';

    if (isSpecial && isFirstCard) {
      // Special + 첫 카드 → 비디오 재생 후 모달
      pendingCardDataRef.current = data;
      setIsVideoOpen(true);
    } else if (isSpecial || isFirstCard) {
      // Special 또는 첫 카드 → CardAcquisitionModal
      dispatch(
        modalActions.openComponentModal({
          component: (
            <CardAcquisitionModal
              cardName={data.card_name}
              metadataIdx={data.metadata_idx}
              packIdx={data.pack_idx}
              point={data.point}
              totalPoint={data.total_point}
              teamSlug={teamSlug}
              designIdx={data.design_idx}
            />
          ),
        }),
      );
    } else {
      // Basic 중복 → CardUnlockSuccessModal
      dispatch(
        modalActions.openComponentModal({
          component: (
            <CardUnlockSuccessModal
              cardName={data.card_name}
              image={data.image}
              metadataIdx={data.metadata_idx}
              packIdx={data.pack_idx}
              point={data.point}
              totalPoint={data.total_point}
              teamSlug={teamSlug}
            />
          ),
        }),
      );
    }

    handleSuccessMint();
  };

  // ============================================================================
  // 코드 입력 민팅
  // ============================================================================

  /** 코드 입력 민팅 Mutation */
  const { mutate: mintWithCodeMutate, isPending: isMinting } = useMintWithCodeMutation({
    onSuccess: async (res) => {
      const { card_name, image, metadata_idx, pack_idx, grade, design_idx, point, total_point } = res.data.data;
      trackEvent({ name: 'redeem_code', params: { input_method: 'manual', success: true, card_name, card_idx: metadata_idx, pack_idx, grade: grade ?? undefined, is_first: true, design_idx: design_idx ?? undefined } });
      if (point > 0) {
        trackEvent({ name: 'earning_point', params: { point, value: point, action_type: 'enter_code', migration_source: 'gtm' } });
      }
      await handleCardAcquisition({ card_name, image, metadata_idx, pack_idx, grade, design_idx, point, total_point });
    },
    onError: (error) => {
      const statusCode = getAxiosErrorStatus(error) ?? '';
      trackEvent({ name: 'redeem_code', params: { input_method: 'manual', success: false, error_code: statusCode } });
      handleCodeError(statusCode);
    },
  });

  /** 코드 입력 핸들러 (대문자 영숫자만 허용, 최대 10자) */
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, CODE_MAX_LENGTH);
    setCode(sanitized);
  };

  /** 코드 등록 폼 제출 */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    requireLogin(() => mintWithCodeMutate({ originalsIdx, checkData: code }));
  };

  // ============================================================================
  // OCR (카메라) 스캔
  // ============================================================================

  /** OCR 스캔 성공 핸들러 */
  const handleScanComplete = async ({ cardName, image, metadataIdx, packIdx, point, totalPoint, grade, designIdx }: OnScanCompleteProps) => {
    trackEvent({ name: 'redeem_code', params: { input_method: 'ocr', success: true, card_name: cardName, card_idx: Number(metadataIdx), pack_idx: packIdx ?? 0, grade: grade ?? undefined, is_first: true, design_idx: designIdx ?? undefined } });
    if (point > 0) {
      trackEvent({ name: 'earning_point', params: { point, value: point, action_type: 'enter_code', migration_source: 'gtm' } });
    }
    await handleCardAcquisition({
      card_name: cardName,
      image,
      metadata_idx: Number(metadataIdx),
      pack_idx: packIdx ?? 0,
      point,
      total_point: totalPoint,
      grade: grade ?? 'Basic',
      design_idx: designIdx ?? null,
    });
  };

  /** OCR 스캔 에러 핸들러 */
  const handleScanError = ({ statusCode, code: scannedCode }: OnScanCancelProps) => {
    trackEvent({ name: 'redeem_code', params: { input_method: 'ocr', success: false, error_code: statusCode } });
    handleCodeError(statusCode, scannedCode);
  };

  /** OCR 모달 열기 */
  const handleOpenOCRModal = () => {
    requireLogin(() => {
      dispatch(
        modalActions.openOCRModal({
          originalsIdx,
          onScanComplete: handleScanComplete,
          onScanCancel: handleScanError,
        }),
      );
    });
  };

  // ============================================================================
  // 비디오 종료 핸들러 (Special + 첫 카드)
  // ============================================================================

  /** 비디오 재생 종료 시 CardAcquisitionModal 표시 */
  const handleVideoEnd = useCallback(() => {
    setIsVideoOpen(false);

    if (pendingCardDataRef.current) {
      dispatch(
        modalActions.openComponentModal({
          component: (
            <CardAcquisitionModal
              cardName={pendingCardDataRef.current.card_name}
              metadataIdx={pendingCardDataRef.current.metadata_idx}
              packIdx={pendingCardDataRef.current.pack_idx}
              point={pendingCardDataRef.current.point}
              totalPoint={pendingCardDataRef.current.total_point}
              teamSlug={teamSlug}
              designIdx={pendingCardDataRef.current.design_idx!}
            />
          ),
        }),
      );
      pendingCardDataRef.current = null;
    }
  }, [dispatch, teamSlug]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      {/* Special + 첫 카드 획득 시 재생되는 풀스크린 비디오 */}
      <FullScreenVideo isOpen={isVideoOpen} teamSlug={teamSlug} onVideoEnd={handleVideoEnd} />

      <form onSubmit={handleSubmit} className="mb-9 flex w-full flex-col items-center gap-2">
        {/* 코드 입력 + 등록 버튼 */}
        <div className="flex w-full items-center gap-2">
          <Input
            value={code}
            onChange={handleCodeChange}
            placeholder="코드를 입력해주세요"
            maxLength={CODE_MAX_LENGTH}
            pattern="[A-Z0-9]*"
            inputMode="text"
            autoCapitalize="off"
          />
          <Button type="submit" disabled={code.length < CODE_MIN_LENGTH || isButtonDisabled || isMinting} className="w-[100px]">
            {eventStatus !== 'after' ? '직접 등록' : '이벤트 종료'}
          </Button>
        </div>

        {/* OCR (카메라) 버튼 */}
        <Button
          variant="code"
          onClick={(e) => {
            e.preventDefault();
            handleOpenOCRModal();
          }}
          disabled={isButtonDisabled}
        >
          <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9.07178 5.57324H15.9292C17.5859 5.57347 18.9292 6.91653 18.9292 8.57324V15.4307C18.929 17.0872 17.5857 18.4304 15.9292 18.4307H9.07178C7.41506 18.4307 6.072 17.0873 6.07178 15.4307V8.57324C6.07178 6.91639 7.41492 5.57324 9.07178 5.57324Z"
              stroke="currentColor"
              strokeWidth="0.857143"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.5003 14.5742C13.9205 14.5742 15.0718 13.423 15.0718 12.0028C15.0718 10.5826 13.9205 9.43136 12.5003 9.43136C11.0802 9.43136 9.92892 10.5826 9.92892 12.0028C9.92892 13.423 11.0802 14.5742 12.5003 14.5742ZM12.5003 15.4314C14.3939 15.4314 15.9289 13.8963 15.9289 12.0028C15.9289 10.1092 14.3939 8.57422 12.5003 8.57422C10.6068 8.57422 9.07178 10.1092 9.07178 12.0028C9.07178 13.8963 10.6068 15.4314 12.5003 15.4314Z"
              fill="currentColor"
            />
            <path
              d="M2.21436 18.498V22.2842H5.99951V23.1416H2.21436C1.74101 23.1416 1.35701 22.7575 1.35693 22.2842V18.498H2.21436ZM23.6431 18.498V22.2842C23.643 22.7575 23.2589 23.1415 22.7856 23.1416H18.9995V22.2842H22.7856V18.498H23.6431ZM5.99951 1.71289H2.21436V5.49805H1.35693V1.71289C1.35693 1.2395 1.74097 0.855469 2.21436 0.855469H5.99951V1.71289ZM22.7856 0.855469C23.259 0.855542 23.6431 1.23955 23.6431 1.71289V5.49805H22.7856V1.71289H18.9995V0.855469H22.7856Z"
              fill="currentColor"
            />
            <path
              d="M2.21436 18.498V22.2842H5.99951V23.1416H2.21436C1.74101 23.1416 1.35701 22.7575 1.35693 22.2842V18.498H2.21436ZM23.6431 18.498V22.2842C23.643 22.7575 23.2589 23.1415 22.7856 23.1416H18.9995V22.2842H22.7856V18.498H23.6431ZM5.99951 1.71289H2.21436V5.49805H1.35693V1.71289C1.35693 1.2395 1.74097 0.855469 2.21436 0.855469H5.99951V1.71289ZM22.7856 0.855469C23.259 0.855542 23.6431 1.23955 23.6431 1.71289V5.49805H22.7856V1.71289H18.9995V0.855469H22.7856Z"
              stroke="currentColor"
            />
          </svg>
          카메라로 등록
        </Button>
      </form>
    </>
  );
}
